import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { showModal, hideReport, showReport } from '../../actions/modal';
import { REPORT_MODAL } from '../../containers/modal/modal-types';
import FluidContainer from '../layouts/components/fluid-container';
import Button from '../button';
import { useWindowSize } from '../../lib/hooks';
import { getSourceLabel } from '../../lib/utils';

import { GotoSourceButton } from '../buttons-bar';
import { SOURCES } from '../../constants/sources';

const HOMEWORK_UNAUTHORIZED_ERROR = 'HOMEWORK_UNAUTHORIZED_ERROR';
const GENERIC_ERROR = 'GENERIC_ERROR';

export const Title = styled.div`
    font-size: 28px;
    font-weight: 300;
    line-height: 36px;
`;

export const Subtitle = styled.div`
    margin-top: 7px;
    font-size: 16px;
    line-height: 24px;
`;

export const Description = styled(Subtitle)`
    margin-top: 32px;
`;

const ButtonsContainer = styled(FluidContainer)`
    margin-top: 32px;
`;

const ButtonContainer = styled.div`
    margin: 0 0 16px 0;
    & > button {
        width: 100%;
    }

    ${(props) => props.theme.mediaQueries.tablet} {
        margin: 0 16px 0 0;
    }
`;

const HomeworkUnauthorizedErrorHeading = ({ source, isLtiLaunch }) => {
    const messageLabel = isLtiLaunch ? `Accedi nuovamente` : `Torna su ${getSourceLabel(source)}, accedi`;
    return (
        <>
            <Title>{'Sembra che tu non possa vedere questa prova'}</Title>
            <Description>
                <>
                    {`${messageLabel} e riapri la prova.`}
                    <br />
                </>
                {'Non riesci ancora a vederla? Contattaci'}
            </Description>
        </>
    );
};

const GenericErrorHeading = ({ source, isLtiLaunch }) => (
    <>
        <Title>{'Oh no, qualcosa è andato storto!'}</Title>
        <Subtitle>{'Non siamo riusciti a caricare gli esercizi.'}</Subtitle>
        <Description>
            {!isLtiLaunch && (
                <>
                    {`Torna su ${getSourceLabel(source)} e seleziona di nuovo la prova da svolgere.`}
                    <br />
                </>
            )}
            {'Hai bisogno di aiuto? Contattaci'}
        </Description>
    </>
);

const ErrorHeading = ({ error, source, isLtiLaunch }) => {
    switch (error) {
        case HOMEWORK_UNAUTHORIZED_ERROR:
            return <HomeworkUnauthorizedErrorHeading source={source} isLtiLaunch={isLtiLaunch} />;
        default:
            return <GenericErrorHeading source={source} isLtiLaunch={isLtiLaunch} />;
    }
};

ErrorHeading.propTypes = {
    error: PropTypes.string,
    isLtiLaunch: PropTypes.bool,
    source: PropTypes.string,
};

export const ExerciseLoadError = ({
    bookId,
    displayReport,
    googleCourseWorkId,
    hideReport,
    homeworkId,
    homeworkErrorCode,
    isLtiLaunch,
    isOpenAssignment,
    showModal,
    showReport,
    source,
}) => {
    const { innerWidth } = useWindowSize();
    const isMobile = innerWidth < 767;

    const handleClickReport = () => {
        if (isMobile) {
            showModal(REPORT_MODAL);
            showReport();
            return;
        }
        if (displayReport) {
            hideReport();
            return;
        }
        showReport();
    };

    const error = homeworkErrorCode && homeworkErrorCode === 401 ? HOMEWORK_UNAUTHORIZED_ERROR : GENERIC_ERROR;

    const backSource = !!googleCourseWorkId ? SOURCES.GOOGLE_CLASSROOM : source;

    return (
        <FluidContainer flexDirection="column" maxWidth={['100%', '50%', '50%']} paddingTop={[100, 144]} px={['base', 0]}>
            <ErrorHeading error={error} source={backSource} isLtiLaunch={isLtiLaunch} />
            <ButtonsContainer flexDirection={['column', 'row', 'row']}>
                {!isLtiLaunch && (
                    <ButtonContainer>
                        <GotoSourceButton
                            bookId={bookId}
                            source={backSource}
                            homeworkId={!isOpenAssignment ? homeworkId : undefined}
                            type="primary"
                        />
                    </ButtonContainer>
                )}
                <ButtonContainer>
                    <Button type="tertiary" onClick={() => handleClickReport()} size="small">
                        {'Contattaci'}
                    </Button>
                </ButtonContainer>
            </ButtonsContainer>
        </FluidContainer>
    );
};

ExerciseLoadError.propTypes = {
    bookId: PropTypes.number,
    displayReport: PropTypes.bool,
    googleCourseWorkId: PropTypes.string,
    hideReport: PropTypes.func,
    homeworkId: PropTypes.string,
    homeworkErrorCode: PropTypes.number,
    isLtiLaunch: PropTypes.bool,
    isOpenAssignment: PropTypes.bool,
    showModal: PropTypes.func,
    showReport: PropTypes.func,
    source: PropTypes.string,
};

GenericErrorHeading.propTypes = {
    isLtiLaunch: PropTypes.bool,
    source: PropTypes.string,
};

HomeworkUnauthorizedErrorHeading.propTypes = {
    isLtiLaunch: PropTypes.bool,
    source: PropTypes.string,
};

const stateToProps = (state) => {
    return {
        bookId: state.application.bookId,
        displayReport: state.modal.showReport,
        googleCourseWorkId: state.application.googleCourseWorkId,
        homeworkId: state.homework.id,
        homeworkErrorCode: state.homework.status.error,
        source: state.application.source,
        isLtiLaunch: state.application.isLtiLaunch,
        isOpenAssignment: state.application.isOpenAssignment,
    };
};
const actionCreators = {
    hideReport,
    showModal,
    showReport,
};
export default connect(stateToProps, actionCreators)(ExerciseLoadError);
